import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import '@ag-grid-enterprise/core';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import esMX from '@angular/common/locales/es-MX';
import frCA from '@angular/common/locales/fr-CA';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
  MatDialogModule
} from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE
} from '@ats/component-ui/date-picker';
import { OdataModule } from '@ats/service-base';
import { AuthModule } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { MessagePopupComponent } from '../app/shared/message-popup/message-popup.component';
import { appRoutes } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { AuthHttpInterceptor } from './core/interceptors/auth.interceptor';
import { RequestTimeoutHttpInterceptor } from './core/interceptors/request-timeout-http.interceptor';
import {
  ToastEncodeInterceptor,
  ToastInterceptor
} from './core/interceptors/toast-interceptor';
import { BannerTranslatePipe } from './core/pipes/banner-translate.pipe';
import { HierarchyTranslatePipe } from './core/pipes/hierarchy-translate.pipe';
import { PauseAlertingWindowLabelPipe } from './core/pipes/pause-alerting-window-label.pipe';
import { AppConfigService } from './core/services/app-config.service';
import { CustomBreakPointsProvider } from './core/utils/providers/custom-breakpoints';
import { AppGlobalErrorhandler } from './lib/error-handler';
import { NotificationsWelcomeModalComponent } from './shared/notifications-welcome-modal/notifications-welcome-modal.component';
import { TranslocoRootModule } from './transloco-root.module';

registerLocaleData(esMX, 'es-mx');
registerLocaleData(frCA, 'fr-ca');

const nativeModules = [
  BrowserAnimationsModule,
  FormsModule,
  BrowserModule,
  appRoutes
];

const appModules = [
  CoreModule,
  MatSidenavModule,
  MatTooltipModule,
  HierarchyTranslatePipe,
  BannerTranslatePipe
];

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  MenuModule,
  ColumnsToolPanelModule,
  ClipboardModule
]);

@NgModule({
  declarations: [AppComponent, PauseAlertingWindowLabelPipe],
  imports: [
    ...nativeModules,
    AuthModule.forRoot(),
    ...appModules,
    MessagePopupComponent,
    NotificationsWelcomeModalComponent,
    MatDialogModule,
    TranslocoRootModule,
    OdataModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AppConfigService],
      useFactory: (AppConfigService: AppConfigService) => {
        return async () => {
          return await firstValueFrom(AppConfigService.loadAppConfig());
        };
      }
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestTimeoutHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ToastEncodeInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ToastInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useClass: AppGlobalErrorhandler
    },
    CustomBreakPointsProvider,
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: () => {
        return (
          navigator.languages ? navigator.languages[0] : navigator['language']
        ) as string;
      }
    },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: () => {
        const lang: string = (
          navigator.languages ? navigator.languages[0] : navigator['language']
        ) as string;

        let retVal = {};
        switch (lang.toLowerCase().substring(0, 2)) {
          case 'en':
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
            break;
          default:
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
        }

        return retVal;
      }
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        role: 'dialog',
        ariaModal: true,
        disableClose: true
      } as MatDialogConfig
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
